'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Button, Input } from '@velux/components-react';

interface OtpVerificationStepProps {
  maskedEmail: string;
  onVerify: (otp: string) => Promise<{ error?: string }>;
  onResend: () => Promise<void>;
  onBack: () => void;
}

export function OtpVerificationStep({
  maskedEmail,
  onVerify,
  onResend,
  onBack,
}: OtpVerificationStepProps) {
  const t = useTranslations('otp');

  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(60);
  const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startResendCooldown = () => {
    setResendDisabled(true);
    setResendCountdown(60);

    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
    }

    resendTimerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current!);
          resendTimerRef.current = null;
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start cooldown on mount
  useEffect(() => {
    startResendCooldown();
    return () => {
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (otpValue.length !== 6) {
      setOtpError(t('invalidLength'));
      return;
    }

    setLoading(true);
    setOtpError('');

    const result = await onVerify(otpValue);

    if (result.error) {
      setOtpError(result.error);
      setLoading(false);
    } else {
      // Parent handles navigation — keep loading but add safety timeout
      // in case navigation fails silently
      setTimeout(() => setLoading(false), 10000);
    }
  };

  const handleResend = async () => {
    if (resendDisabled) return;

    await onResend();
    setOtpValue('');
    setOtpError('');
    startResendCooldown();
  };

  const handleBack = () => {
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    onBack();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary py-8 px-4">
      <div className="w-full max-w-xl bg-white p-8 shadow-card">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            {t('title')}
          </h1>
          <p className="text-text-secondary">
            {t('subtitle', { email: maskedEmail })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <Input
            id="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otpValue}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtpValue(val);
              if (otpError) setOtpError('');
            }}
            error={otpError || undefined}
            placeholder={t('placeholder')}
            maxLength={6}
            autoFocus
          />

          <Button
            type="submit"
            variant="professional-red"
            disabled={loading || otpValue.length !== 6}
            className="w-full"
          >
            {loading ? t('verifying') : t('verifyButton')}
          </Button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={handleBack}
              className="text-velux-red hover:underline"
            >
              {t('backToForm')}
            </button>

            <button
              type="button"
              onClick={handleResend}
              disabled={resendDisabled}
              className={`${resendDisabled ? 'text-text-tertiary cursor-not-allowed' : 'text-velux-red hover:underline'}`}
            >
              {resendDisabled
                ? t('resendIn', { seconds: resendCountdown.toString() })
                : t('resendButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
